@if (error) {
  <p class="qtl-status error">{{ error }}</p>
} @else if (isFetching) {
  <p class="qtl-status">Loading the variant…</p>
} @else if (variant) {

  <div class="variant-head">
    <div>
      <h3 class="variant-name">{{ variant.variant }}</h3>
      <p class="variant-sub">
        {{ variant.contig }}:{{ variant.pos | number }}
        · MAF {{ variant.maf | percent:'1.1-1' }}
        @if (variant.gene) {
          · {{ variant.feature }} of <strong>{{ variant.gene }}</strong>
          @if (variant.distance_to_gene) {
            ({{ variant.distance_to_gene | number:'1.0-0' }} bp away)
          }
        }
      </p>
      @if (outsideLinks.length) {
        <p class="outside">
          Also known as
          @for (link of outsideLinks; track link.url) {
            <a [href]="link.url" [title]="link.title"
               target="_blank" rel="noopener">{{ link.label }}</a>
          }
        </p>
      } @else if (dbsnp?.applies && !dbsnp?.mapped) {
        <p class="outside none">
          Not in the dbSNP map, so it has no rsID to link to. It was tested all
          the same.
        </p>
      }
    </div>
    @if (plottedAsc) {
      <button type="button" class="back-to-gene" (click)="backToGene.emit()"
              [title]="'Back to the scan for ' + ascName(selection?.locus, plottedAsc)">
        ← {{ ascName(selection?.locus, plottedAsc) }} scan
      </button>
    }
  </div>

  <div class="variant-grid">

    <section class="card">
      <h4>Usage by genotype</h4>
      @if (!plottedAsc) {
        <p class="note">Choose an ASC to see the usage this variant explains.</p>
      } @else if (!subjects.length) {
        <p class="note">No genotypes are held for this variant.</p>
      } @else {
        <plotly-plot [data]="plotData" [layout]="plotLayout" [config]="plotConfig"></plotly-plot>
        @if (association) {
          <p class="stat">
            effect {{ association.beta | number:'1.3-3' }}
            · p {{ association.p_value.toExponential(2) }}
            · n {{ association.n }}
            @if (association.min_genotype_group !== null) {
              <span [class.weak]="association.well_powered === false">
                · smallest genotype class {{ association.min_genotype_group }}
              </span>
            }
          </p>
        }
      }
    </section>

    <section class="card">
      <h4>Genotype distribution</h4>
      @if (!counts.length) {
        <p class="note">No genotypes are held for this variant.</p>
      } @else {
        <app-qtl-genotype-counts [counts]="counts" [height]="440"
                                 [variant]="variant.variant"
                                 [source]="'/api/qtl/variant/' + selection?.species
                                           + '/' + selection?.locus + '/' + variant.variant">
        </app-qtl-genotype-counts>
        <p class="note">
          How many subjects carry each genotype. A class with only a handful of
          subjects cannot support a confident effect estimate, however small the
          p-value is.
        </p>
      }
    </section>

    <!-- Above the table, not below it. This page was reached from one gene's
         scan and the question in hand is what this variant is; the table is the
         wider follow-up, so it comes after. -->
    <section class="card wide">
      <h4>Around {{ variant.variant }}</h4>
      <app-qtl-region [selection]="selection" [fallbackAsc]="fallbackAsc"
                      (variantPicked)="regionVariantPicked.emit($event)">
      </app-qtl-region>
    </section>

    <section class="card wide">
      <h4>Every gene this variant was tested against</h4>
      <p class="note">
        Each gene is its own scan, so a variant has one result per gene. Click one
        to plot that gene's usage instead. Strongest first, <strong>{{ associations.length }}</strong> genes,
        <strong>{{ significantCount }}</strong> reaching significance.
        @if (significantCount) {
          <label class="sig-toggle">
            <input type="checkbox" [checked]="significantOnly"
                   (change)="significantOnly = !significantOnly" />
            significant only
          </label>
        }
      </p>
      <div class="assoc-scroll">
      <table class="assoc">
        <tr>
          <th>gene</th><th>seg</th><th class="n">-log10 p</th><th class="n">effect</th>
          <th class="n">n</th><th class="n">smallest class</th><th></th>
        </tr>
        @for (a of shownAssociations; track a.asc) {
          <tr [class.on]="a.asc === plottedAsc">
            <td><button type="button" class="link-button"
                        (click)="ascPicked.emit(a.asc)">{{ a.asc }}</button></td>
            <td class="muted">{{ a.segment }}</td>
            <td class="n">{{ a.neglog10_p | number:'1.1-1' }}</td>
            <td class="n">{{ a.beta | number:'1.2-2' }}</td>
            <td class="n muted">{{ a.n }}</td>
            <td class="n" [class.weak]="a.well_powered === false">
              {{ a.min_genotype_group ?? '–' }}
            </td>
            <td>
              @if (a.significant) { <span class="tag sig">significant</span> }
              @if (a.is_lead) { <span class="tag lead">lead</span> }
            </td>
          </tr>
        }
      </table>
      </div>
      @if (significantOnly && significantCount < associations.length) {
        <p class="note">
          Showing {{ significantCount }} of {{ associations.length }} genes. The
          rest were tested and did not reach the threshold, they are results,
          not absences.
        </p>
      }
    </section>
  </div>
} @else {
  <p class="qtl-status">Click a point on the scan to open a variant.</p>
}
